import { Injectable, isDevMode } from '@angular/core';
import { EMPTY, Subject } from 'rxjs';
import { map } from 'rxjs/internal/operators/map';
import { tap } from 'rxjs/internal/operators/tap';
import { SmsDaoService } from 'src/app/dao/sms-dao-service/sms-dao.service';
import { CenterLite } from 'src/app/models/center-model/center-lite.model';
import { SMSBasic } from 'src/app/models/turns-models/turns-sms-basic.model';
import { UserService } from 'src/app/services/user-service/user.service';
import { UserStorageService } from '../user-storage-service/user-storage.service';

@Injectable({
  providedIn: 'root'
})
export class SmsService {

  accessToken = 'SMS';
  refreshSmsListListener = new Subject();
  refreshSmsListener = new Subject();
  searchSmsListener = new Subject<any>();
  filterSmsListener = new Subject<any>();
  toggleSmssDispositionListener = new Subject<any>();

  constructor(private smsDesksTurnsDaoService: SmsDaoService,
              private userStorageService: UserStorageService,
              private userService: UserService) { }

  public getSmsList() {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.smsDesksTurnsDaoService.getSmsList(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID()).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const smss = new Array<SMSBasic>();
          o['data']['smsList'].forEach(
            (s) => {
              const sms = new SMSBasic();
              Object.assign(sms, s);

              const centers = new Array<CenterLite>();
              s['centers'].forEach(c => {
                const center = new CenterLite();
                Object.assign(center, c);
                centers.push(center);
              });
              sms.Centers = centers;

              smss.push(sms);
            }
          );
          return smss;
        })
      );
    }
    return EMPTY;
  }

  public getSmsCounter() {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.smsDesksTurnsDaoService.getSmsCounter(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID()).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          return {
            availableSms: o['data']['counters']['nbAvailables'],
            unavailableSms: o['data']['counters']['nbDisabled']
          };
        })
      );
    }
    return EMPTY;
  }

  public getSmsInfo(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.smsDesksTurnsDaoService.getSmsInfo(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const sms = new SMSBasic();
          Object.assign(sms, o['data']['smsDetails']);
          const centers = new Array<CenterLite>();
          o['data']['smsDetails']['centers'].forEach(c => {
            const center = new CenterLite();
            Object.assign(center, c);
            centers.push(center);
          });
          sms.Centers = centers;
          return sms;
        })
      );
    }
    return EMPTY;
  }

  public addNewSms(smsName, centerInternalIds, active, smsSubject, smsPickAppointment, smsPickTurn, smsPreviousTurn, smsArrivalTurn) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.smsDesksTurnsDaoService.addNewSms(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), smsName, centerInternalIds, active, smsSubject, smsPickAppointment, smsPickTurn, smsPreviousTurn, smsArrivalTurn).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateSmsInfo(internalId, smsName, active, smsSubject) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.smsDesksTurnsDaoService.updateSmsInfo(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, smsName, active, smsSubject).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateSmsCenters(internalId, centerInternalIds) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.smsDesksTurnsDaoService.updateSmsCenters(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, centerInternalIds).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateSmsTemplates(internalId, active, smsPickAppointment, smsPickTurn, smsPreviousTurn, smsArrivalTurn) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.smsDesksTurnsDaoService.updateSmsTemplates(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, active, smsPickAppointment, smsPickTurn, smsPreviousTurn, smsArrivalTurn).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateSmsStatus(internalId, active) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.smsDesksTurnsDaoService.updateSmsStatus(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, active).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public deleteSms(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.smsDesksTurnsDaoService.deleteSms(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public refreshSmsListListen() {
      return this.refreshSmsListListener.asObservable();
  }
  public refreshSmsList() {
    this.refreshSmsListListener.next();
  }
  public refreshSmsListen() {
      return this.refreshSmsListener.asObservable();
  }
  public refreshSms() {
    this.refreshSmsListener.next();
  }
  public searchSms(filterKey) {
    this.searchSmsListener.next(filterKey);
  }

  public searchSmsListen() {
    return this.searchSmsListener.asObservable();
  }
  public filterSms(opts) {
    this.filterSmsListener.next(opts);
  }

  public filterSmsListen() {
    return this.filterSmsListener.asObservable();
  }

  public toggleSmssDisposition(viewDis) {
    this.toggleSmssDispositionListener.next(viewDis);
  }

  public toggleSmssDispositionListen() {
    return this.toggleSmssDispositionListener.asObservable();
  }
}
